import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone } from 'lucide-react';

import { ApiError } from '@starling/core/api/errors';
import {
  ChoiceRow,
  FormError,
  GhostButton,
  Hint,
  PasswordField,
  PrimaryButton,
  TextField,
} from '@/components/form/primitives';
import { GoogleSignInButton } from './GoogleSignInButton';
import {
  useEmailLoginMutation,
  useEmailSignupRequestCodeMutation,
  useEmailSignupVerifyMutation,
  usePhoneRequestCodeMutation,
  usePhoneVerifyMutation,
  useSession,
} from './hooks';
import type { LoginLocationState } from './RequireAuth';
import styles from './LoginPage.module.css';

// Экран входа/регистрации Vision (два режима, переключатель внизу карточки).
// Вход: email + пароль — основной путь (оптики уже в Light, на SMS экономим),
// телефон + SMS-код, Google. Регистрация: телефон (самозапись V1.3-a, поле
// имени; дефолт), email + пароль + код из письма (самозапись, доводка приёмки
// V1) или Google (самозапись — доводка приёмки V1).
// Google-кнопка — за VITE_GOOGLE_CLIENT_ID. После входа — путь возврата из гарда либо
// «Моя организация» (единственный экран за сессией в V1.3).

type Mode = 'login' | 'signup';
type Method = 'phone' | 'email';
type Stage = 'request' | 'code';

const DEFAULT_TARGET = '/my/organization';

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useSession();

  const from = (location.state as LoginLocationState | null)?.from;
  const target = from ?? DEFAULT_TARGET;

  const [mode, setMode] = useState<Mode>('login');
  const [method, setMethod] = useState<Method>('email');
  const [error, setError] = useState<string | null>(null);

  // Телефон + код.
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<Stage>('request');
  const [resent, setResent] = useState(false);
  const requestMutation = usePhoneRequestCodeMutation();
  const verifyMutation = usePhoneVerifyMutation();

  // Email + пароль (вход) / email + пароль + код из письма (регистрация).
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailStage, setEmailStage] = useState<Stage>('request');
  const [emailCode, setEmailCode] = useState('');
  const [emailResent, setEmailResent] = useState(false);
  const emailMutation = useEmailLoginMutation();
  const signupRequestMutation = useEmailSignupRequestCodeMutation();
  const signupVerifyMutation = useEmailSignupVerifyMutation();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, navigate, target]);

  const mapError = (err: unknown, invalidKey: string): string => {
    if (err instanceof ApiError) {
      if (err.code === 'email_taken') return t('auth.errors.emailTaken');
      if (err.code === 'invalid_phone' || err.status === 422) return t('auth.errors.invalidPhone');
      if (err.status === 400 || err.status === 401) return t(invalidKey);
    }
    return t('auth.errors.generic');
  };

  const submitRequestCode = (resend = false): void => {
    setError(null);
    setResent(false);
    requestMutation.mutate(
      { phone: phone.trim() },
      {
        onSuccess: () => {
          if (resend) setResent(true);
          else setStage('code');
        },
        onError: (err) => setError(mapError(err, 'auth.errors.generic')),
      },
    );
  };

  const submitVerify = (): void => {
    setError(null);
    verifyMutation.mutate(
      { phone: phone.trim(), code: code.trim(), name: name.trim() || null },
      {
        onError: (err) => setError(mapError(err, 'auth.errors.invalidCode')),
      },
    );
  };

  const submitEmailSignupRequest = (resend = false): void => {
    setError(null);
    setEmailResent(false);
    signupRequestMutation.mutate(
      { email: email.trim() },
      {
        onSuccess: () => {
          if (resend) setEmailResent(true);
          else setEmailStage('code');
        },
        onError: (err) => setError(mapError(err, 'auth.errors.generic')),
      },
    );
  };

  const submitEmailSignupVerify = (): void => {
    setError(null);
    signupVerifyMutation.mutate(
      { email: email.trim(), code: emailCode.trim(), password, name: name.trim() || null },
      {
        onError: (err) => setError(mapError(err, 'auth.errors.invalidCode')),
      },
    );
  };

  const submitEmail = (): void => {
    setError(null);
    emailMutation.mutate(
      { email: email.trim(), password },
      {
        onError: (err) => setError(mapError(err, 'auth.errors.invalidCredentials')),
      },
    );
  };

  const pending =
    requestMutation.isPending ||
    verifyMutation.isPending ||
    emailMutation.isPending ||
    signupRequestMutation.isPending ||
    signupVerifyMutation.isPending;

  const switchMode = (next: Mode): void => {
    setMode(next);
    // Email — приоритетный канал в обоих режимах (оптикам привычнее,
    // на SMS экономим); телефон — второй способ (путь частников).
    setMethod('email');
    setError(null);
    setStage('request');
    setCode('');
    setResent(false);
    setEmailStage('request');
    setEmailCode('');
    setEmailResent(false);
  };

  const phoneVisible = method === 'phone';

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <h1 className={styles.title}>{t(mode === 'login' ? 'auth.title' : 'auth.signupTitle')}</h1>
        <p className={styles.subtitle}>
          {t(mode === 'login' ? 'auth.subtitle' : 'auth.signupSubtitle')}
        </p>

        <ChoiceRow<Method>
          value={method}
          onChange={(next) => {
            setMethod(next);
            setError(null);
          }}
          label={t(mode === 'login' ? 'auth.methodLabel' : 'auth.methodSignupLabel')}
          options={[
            { value: 'email', label: t('auth.methodEmail') },
            { value: 'phone', label: t('auth.methodPhone') },
          ]}
        />

        <FormError>{error}</FormError>

        {phoneVisible && stage === 'request' && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitRequestCode();
            }}
          >
            <TextField
              label={t('auth.phone.label')}
              value={phone}
              onChange={setPhone}
              type="tel"
              inputMode="tel"
              name="phone"
              autoComplete="tel"
              placeholder={t('auth.phone.placeholder')}
              maxLength={32}
            />
            {mode === 'signup' && (
              <TextField
                label={t('auth.phone.name')}
                value={name}
                onChange={setName}
                name="name"
                autoComplete="name"
                placeholder={t('auth.phone.namePlaceholder')}
                maxLength={200}
              />
            )}
            <PrimaryButton disabled={pending || phone.trim().length < 10}>
              {t('auth.phone.requestCode')}
            </PrimaryButton>
          </form>
        )}

        {phoneVisible && stage === 'code' && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitVerify();
            }}
          >
            <p className={styles.codeHint}>
              <Phone size={13} aria-hidden="true" />
              {t('auth.phone.codeSent', { phone: phone.trim() })}
            </p>
            <Hint>{t('auth.phone.codeHint')}</Hint>
            <TextField
              label={t('auth.phone.codeLabel')}
              value={code}
              onChange={setCode}
              inputMode="numeric"
              name="code"
              autoComplete="one-time-code"
              placeholder="••••••"
              maxLength={6}
            />
            <PrimaryButton disabled={pending || code.trim().length !== 6}>
              {t(mode === 'signup' ? 'auth.phone.submitSignup' : 'auth.phone.submit')}
            </PrimaryButton>
            {resent && <div className={styles.resent}>{t('auth.phone.resent')}</div>}
            <div className={styles.secondaryRow}>
              <GhostButton
                disabled={pending}
                onClick={() => {
                  setStage('request');
                  setCode('');
                  setError(null);
                  setResent(false);
                }}
              >
                {t('auth.phone.changePhone')}
              </GhostButton>
              <GhostButton disabled={pending} onClick={() => submitRequestCode(true)}>
                {t('auth.phone.resend')}
              </GhostButton>
            </div>
          </form>
        )}

        {mode === 'signup' && method === 'email' && emailStage === 'request' && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitEmailSignupRequest();
            }}
          >
            <TextField
              label={t('auth.email.label')}
              value={email}
              onChange={setEmail}
              type="email"
              inputMode="email"
              name="email"
              autoComplete="email"
              placeholder="you@example.com"
            />
            <PasswordField
              label={t('auth.email.password')}
              value={password}
              onChange={setPassword}
              name="password"
              autoComplete="new-password"
            />
            <Hint>{t('auth.emailSignup.passwordHint')}</Hint>
            <TextField
              label={t('auth.phone.name')}
              value={name}
              onChange={setName}
              name="name"
              autoComplete="name"
              placeholder={t('auth.phone.namePlaceholder')}
              maxLength={200}
            />
            <PrimaryButton disabled={pending || !email.trim() || password.length < 8}>
              {t('auth.emailSignup.requestCode')}
            </PrimaryButton>
          </form>
        )}

        {mode === 'signup' && method === 'email' && emailStage === 'code' && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitEmailSignupVerify();
            }}
          >
            <p className={styles.codeHint}>
              <Mail size={13} aria-hidden="true" />
              {t('auth.emailSignup.codeSent', { email: email.trim() })}
            </p>
            <Hint>{t('auth.emailSignup.codeHint')}</Hint>
            <TextField
              label={t('auth.emailSignup.codeLabel')}
              value={emailCode}
              onChange={setEmailCode}
              inputMode="numeric"
              name="emailCode"
              autoComplete="one-time-code"
              placeholder="••••••"
              maxLength={6}
            />
            <PrimaryButton disabled={pending || emailCode.trim().length !== 6}>
              {t('auth.emailSignup.submit')}
            </PrimaryButton>
            {emailResent && <div className={styles.resent}>{t('auth.emailSignup.resent')}</div>}
            <div className={styles.secondaryRow}>
              <GhostButton
                disabled={pending}
                onClick={() => {
                  setEmailStage('request');
                  setEmailCode('');
                  setError(null);
                  setEmailResent(false);
                }}
              >
                {t('auth.emailSignup.changeEmail')}
              </GhostButton>
              <GhostButton disabled={pending} onClick={() => submitEmailSignupRequest(true)}>
                {t('auth.emailSignup.resend')}
              </GhostButton>
            </div>
          </form>
        )}

        {mode === 'login' && method === 'email' && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitEmail();
            }}
          >
            <TextField
              label={t('auth.email.label')}
              value={email}
              onChange={setEmail}
              type="email"
              inputMode="email"
              name="email"
              autoComplete="email"
              placeholder="you@example.com"
            />
            <PasswordField
              label={t('auth.email.password')}
              value={password}
              onChange={setPassword}
              name="password"
              autoComplete="current-password"
            />
            <div className={styles.forgotRow}>
              <Link to="/forgot-password" className={styles.switchLink}>
                {t('auth.forgotPassword')}
              </Link>
            </div>
            <PrimaryButton disabled={pending || !email.trim() || !password}>
              {t('auth.email.submit')}
            </PrimaryButton>
          </form>
        )}

        <GoogleSignInButton />

        <div className={styles.switchRow}>
          <span>{t(mode === 'login' ? 'auth.noAccount' : 'auth.haveAccount')}</span>
          <button
            type="button"
            className={styles.switchLink}
            onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')}
          >
            {t(mode === 'login' ? 'auth.toSignup' : 'auth.toLogin')}
          </button>
        </div>
      </div>
    </div>
  );
}
